import { format } from "date-fns";
import { useMemo } from "react";

import DateRangePicker from "@/components/DateRangePicker";
import SingleSelect from "@/components/input-select-store";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetStoresOptionsWithDetailsQuery } from "@/services/store/select-options";

import { usePriceRemarkFilterActions } from "../../ducks/filter/hooks/actions";
import { usePriceRemarkFilterState } from "../../ducks/filter/hooks/filterState";
import { formatLojaLabel } from "../../utils/format-loja";
import { parseDateStringLocal } from "../../utils/parse-date";

interface PriceRemarkFilterProps {
  showStore?: boolean;
  storeRequired?: boolean;
  showTipo?: boolean;
  showStatus?: boolean;
}

const ALL = "__todos__";

/**
 * Barra de filtros compartilhada, no padrão do projeto: `DateRangePicker`
 * (período), filtro de loja reutilizado (W03EST), e selects de tipo/status.
 * Estado no slice redux priceRemarkFilter. Qualquer mudança reseta a paginação.
 */
export function PriceRemarkFilter({
  showStore = true,
  storeRequired = false,
  showTipo = true,
  showStatus = false,
}: PriceRemarkFilterProps) {
  const { filters } = usePriceRemarkFilterState();
  const { updateFilter } = usePriceRemarkFilterActions();
  const { data: storeDetails = [] } = useGetStoresOptionsWithDetailsQuery();

  const storeOptions = useMemo(
    () =>
      storeDetails.map((store) => ({
        value: store.value,
        label: formatLojaLabel({
          loja: Number(store.value),
          cidade: store.city,
          uf: store.state,
        }),
      })),
    [storeDetails],
  );

  const selectedStore = storeOptions.find(
    (option) => String(option.value) === String(filters.loja),
  );

  const resetPage = () => updateFilter({ key: "page", value: 1 });

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium text-gray-700">Período</Label>
        <DateRangePicker
          allowUnlimited
          buttonClassName="h-8 w-[280px]"
          date={{
            from: parseDateStringLocal(filters.dtIni),
            to: parseDateStringLocal(filters.dtFim),
          }}
          onDateChange={(range) => {
            if (!range) return;
            updateFilter({ key: "dtIni", value: format(range.from, "yyyy-MM-dd") });
            updateFilter({ key: "dtFim", value: format(range.to, "yyyy-MM-dd") });
            resetPage();
          }}
        />
      </div>

      {showStore && (
        <div className="w-[260px]">
          <SingleSelect
            label={storeRequired ? "Loja (obrigatória)" : "Loja"}
            options={storeOptions}
            selectedValue={selectedStore}
            placeholder="Selecionar loja"
            allowClear={!storeRequired}
            onChange={(option) => {
              updateFilter({
                key: "loja",
                value: option ? Number(option.value) : null,
              });
              resetPage();
            }}
          />
        </div>
      )}

      {showTipo && (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium text-gray-700">Tipo</Label>
          <Select
            value={filters.tipo ?? ALL}
            onValueChange={(value) => {
              updateFilter({ key: "tipo", value: value === ALL ? null : value });
              resetPage();
            }}
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="promocao">Promoção</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showStatus && (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium text-gray-700">Status</Label>
          <Select
            value={filters.status ?? ALL}
            onValueChange={(value) => {
              updateFilter({
                key: "status",
                value: value === ALL ? null : value,
              });
              resetPage();
            }}
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              <SelectItem value="EFE">EFE (efetivada)</SelectItem>
              <SelectItem value="STA">STA (invalidada)</SelectItem>
              <SelectItem value="ERR">ERR (falha)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default PriceRemarkFilter;
